import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import ModalCloseButton from '../common/ModalCloseButton';
import { Heading, Text } from '../common/Typography';
import { HOMEPAGE_MODE } from '../../utils/homePageMode';
import { interactiveBleedInsetX } from '../../constants/interactiveSafeInsets';
import { homeSectionHeading, quickActionSubtitle } from './homeDashboardStyles';
import { HomePerformancePickerModal } from './HomePerformancePickerModal';

const quickActionIconWell =
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-subtle text-xl ring-1 ring-border-light/70 dark:bg-surface-subtle dark:ring-border-medium';

/** @param {'discover-sessions' | 'my-clubs' | 'create-session' | 'stats' | 'create-club'} id */
function accentFor(id) {
  const base =
    'border border-border-light bg-surface shadow-brand-md hover:shadow-brand-lg hover:-translate-y-0.5 ring-2 ring-transparent focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:border-border-medium dark:focus-visible:ring-offset-background';
  switch (id) {
    case 'discover-sessions':
      return `${base} border-l-[3px] border-l-info hover:ring-info/15`;
    case 'my-clubs':
      return `${base} border-l-[3px] border-l-success hover:ring-success/15`;
    case 'create-session':
      return `${base} border-l-[3px] border-l-primary hover:ring-primary/15`;
    case 'stats':
      return `${base} border-l-[3px] border-l-warning hover:ring-warning/15`;
    case 'create-club':
      return `${base} border-l-[3px] border-l-secondary hover:ring-secondary/15`;
    default:
      return `${base} border-l-[3px] border-l-primary`;
  }
}

function subtitleFor(id, canShowCreateSession, appClubsTotal) {
  switch (id) {
    case 'discover-sessions':
      return 'Find upcoming games';
    case 'my-clubs':
      if (typeof appClubsTotal === 'number' && !Number.isNaN(appClubsTotal)) {
        const n = appClubsTotal.toLocaleString();
        return appClubsTotal === 1 ? '1 club ' : `${n} clubs`;
      }
      return 'Browse communities';
    case 'create-session':
      return canShowCreateSession ? 'Start a session' : 'For club hosts';
    case 'stats':
      return 'Games & club stats';
    case 'create-club':
      return 'Build your community';
    default:
      return '';
  }
}

/**
 * HomeQuickActions — colourful feature strips in the dashboard hero band.
 */
export function HomeQuickActions({
  canCreateSession,
  clubsForSessionCreate,
  performanceClubs = [],
  isSuperAdmin,
  homepageMode = HOMEPAGE_MODE.ACTIVE,
  hint,
  appClubsTotal = null,
  className = '',
}) {
  const navigate = useNavigate();
  const [createClubPickerOpen, setCreateClubPickerOpen] = useState(false);
  const [performancePickerOpen, setPerformancePickerOpen] = useState(false);

  const clubs = useMemo(
    () => (Array.isArray(clubsForSessionCreate) ? clubsForSessionCreate : []),
    [clubsForSessionCreate]
  );
  const canShowCreateSession = canCreateSession && clubs.length > 0;

  const navigateToClubStartSessionCreate = useCallback(
    (clubId) => {
      navigate(`/club/${clubId}/sessions/start?quick=1`, {
        state: { fromHomepage: true }
      });
    },
    [navigate]
  );

  const handleCreateSessionClick = useCallback(() => {
    if (clubs.length === 0) return;
    if (clubs.length === 1) {
      navigateToClubStartSessionCreate(clubs[0].clubId);
      return;
    }
    setCreateClubPickerOpen(true);
  }, [clubs, navigateToClubStartSessionCreate]);

  const handlePickClub = useCallback(
    (clubId) => {
      setCreateClubPickerOpen(false);
      navigateToClubStartSessionCreate(clubId);
    },
    [navigateToClubStartSessionCreate]
  );

  const actions = useMemo(() => {
    const discoverSessions = {
      id: 'discover-sessions',
      label: 'Discover Sessions',
      icon: (
        <span className={quickActionIconWell} aria-hidden>
          🔍
        </span>
      ),
      onClick: () => navigate('/upcoming-sessions'),
      show: true
    };
    const myClubs = {
      id: 'my-clubs',
      label: 'Discover Clubs',
      icon: (
        <span className={quickActionIconWell} aria-hidden>
          👥
        </span>
      ),
      onClick: () => navigate('/clubs'),
      show: true
    };
    const createSession = {
      id: 'create-session',
      label: 'Create Session',
      icon: (
        <span className={quickActionIconWell} aria-hidden>
          ➕
        </span>
      ),
      onClick: handleCreateSessionClick,
      show: canShowCreateSession
    };
    const stats = {
      id: 'stats',
      label: 'Stats / Performance',
      icon: (
        <span className={quickActionIconWell} aria-hidden>
          📊
        </span>
      ),
      onClick: () => setPerformancePickerOpen(true),
      show: true
    };
    const createClub = {
      id: 'create-club',
      label: 'Create Club',
      icon: (
        <span className={quickActionIconWell} aria-hidden>
          🏢
        </span>
      ),
      onClick: () => navigate('/create-club'),
      show: isSuperAdmin
    };

    const ordered =
      homepageMode === HOMEPAGE_MODE.ONBOARDING
        ? [myClubs, discoverSessions, stats, createSession, createClub]
        : [discoverSessions, myClubs, stats, createSession, createClub];

    return ordered.filter((a) => a.show);
  }, [
    navigate,
    handleCreateSessionClick,
    canShowCreateSession,
    isSuperAdmin,
    homepageMode,
  ]);

  const gridClass =
    actions.length >= 5
      ? 'grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 xl:grid-cols-5'
      : 'grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4';

  return (
    <>
      <div className={className}>
        <Heading level={2} className={homeSectionHeading}>
          Quick Actions
        </Heading>
        {hint ? (
          <Text className="mb-6 mt-2 text-sm leading-relaxed max-w-2xl block text-text-secondary">
            {hint}
          </Text>
        ) : (
          <p className="mb-6 mt-1 h-px w-24 rounded-full bg-border-medium opacity-80" aria-hidden />
        )}
        <div
          className={`${interactiveBleedInsetX} mt-5 pb-2 pt-1 sm:pb-2.5 sm:pt-1.5`}
        >
          <div className={gridClass}>
          {actions.map((action) => {
            const subtitle = subtitleFor(
              action.id,
              canShowCreateSession,
              appClubsTotal
            );
            return (
              <button
                key={action.id}
                type="button"
                className={`group rounded-2xl p-4 text-left text-text-primary outline-none transition-all duration-200 md:min-h-[6.85rem] min-h-[6rem] touch-manipulation active:scale-[0.985] disabled:opacity-55 ${accentFor(action.id)}`}
                onClick={action.onClick}
              >
                <div className="flex items-start gap-3">
                  {action.icon}
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold leading-snug sm:text-[0.9375rem] text-text-primary">
                      {action.label}
                    </span>
                    {subtitle ? (
                      <span className={`mt-1.5 ${quickActionSubtitle}`}>
                        {subtitle}
                      </span>
                    ) : null}
                  </span>
                </div>
              </button>
            );
          })}
          </div>
        </div>
      </div>

      <HomePerformancePickerModal
        isOpen={performancePickerOpen}
        onClose={() => setPerformancePickerOpen(false)}
        clubs={performanceClubs}
      />

      <Modal isOpen={createClubPickerOpen} onClose={() => setCreateClubPickerOpen(false)}>
        <ModalContent size="small" className="mx-4" aria-labelledby="create-session-club-picker-title">
          <ModalCloseButton onClose={() => setCreateClubPickerOpen(false)} />
          <Heading id="create-session-club-picker-title" level={3} className="mb-2 pr-8">
            Which club?
          </Heading>
          <Text className="text-text-secondary text-sm mb-4">
            Choose the club this session belongs to.
          </Text>
          <ul className="flex flex-col gap-2 max-h-[min(60vh,24rem)] overflow-y-auto">
            {clubs.map(({ clubId, name, location }) => (
              <li key={clubId}>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start text-left h-auto py-3 px-4 whitespace-normal"
                  onClick={() => handlePickClub(clubId)}
                >
                  <span className="flex flex-col items-start gap-0.5 w-full min-w-0">
                    <span className="font-medium truncate w-full">{name}</span>
                    {location ? (
                      <span className="text-xs text-text-secondary font-normal line-clamp-2">
                        {location}
                      </span>
                    ) : null}
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        </ModalContent>
      </Modal>
    </>
  );
}
